import React from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useMovementDocumentUpload } from "../hooks/useMovementDocumentUpload";
import type { MovementServiceFormData } from "../schema/movementServiceFormSchema";
import { ALLOWED_EXTENSIONS } from "./utils";

const LANG_BASE = "vessel-call.movement-services";
const FIELD_NAME = "h2s" as const;

const H2S: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<MovementOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();

  const { files, setFiles, handleFieldChangeForAzure, removeFileAtIndex, isLoading } =
    useMovementDocumentUpload({
      fieldName: FIELD_NAME,
      certificateType: "H2S",
      formData,
      updateFormData,
    });

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.sections.h2s`)}>
        <div className="col-span-full">
          <FormItem<MovementServiceFormData, typeof FIELD_NAME>
            name={FIELD_NAME}
            type="file"
            testId="movement-h2s"
            uploadConditions={FILE_UPLOAD_PROFILES.files}
            allowedFileExtensions={ALLOWED_EXTENSIONS}
            maxFiles={1}
            files={files}
            setFiles={setFiles}
            uploadDescription={t("upload.additional-documents-upload", {
              extensions: ALLOWED_EXTENSIONS,
              size: uploadConditions.maxSizeLabel,
            })}
            onUpload={handleFieldChangeForAzure}
          />
        </div>
        {files.length
          ? files.map((file, idx) => (
              <div
                className="col-span-full"
                key={`${file.name}-${(file as File).lastModified ?? idx}`}
              >
                <SingleFile
                  testId={`${FIELD_NAME}-${idx}`}
                  file={file}
                  currentFileIndex={idx}
                  fieldName={FIELD_NAME}
                  setFiles={() => removeFileAtIndex(idx)}
                  isLoading={isLoading && file instanceof File}
                />
              </div>
            ))
          : null}
      </FormSection>
    </FormProvider>
  );
};

export default H2S;
